<template>
  <div :class="$style.pdfPreview">
    <SCAIconButton
      v-if="onClose"
      :class="$style.mobileCloseButton"
      appearance="inverted"
      type="ghost"
      size="xsmall"
      icon="phosphor/x"
      @click="onClose()"
    />
    <iframe :src="fileUrl" frameborder="0" allow="fullscreen" />
    <!-- Clicks inside the browser's PDF viewer never reach this document, so the layer takes them. -->
    <div
      v-if="expandOnClick"
      :class="[$style.clickLayer, { [$style.passThrough]: isScrolling }]"
      test-id="pdf-preview-click-layer"
      @click="emit('content-click')"
      @wheel="isScrolling = true"
    />
  </div>
</template>

<script lang="ts" setup>
import { SCAIconButton } from "@scayla/components";
import { refAutoReset } from "@vueuse/core";

withDefaults(
  defineProps<{
    fileUrl: string;
    onClose?: () => void;
    /** Covers the document below the viewer's toolbar and emits `content-click` when it is clicked. */
    expandOnClick?: boolean;
  }>(),
  { onClose: undefined, expandOnClick: false },
);

const emit = defineEmits<{ "content-click": [] }>();

// The layer can't scroll the viewer, so it steps aside for the wheel; the first tick is lost.
const isScrolling = refAutoReset(false, 400);
</script>

<style lang="scss" module>
// The browser's PDF viewer keeps its toolbar and scrollbar usable outside the layer.
$chrome-viewer-toolbar-height: 56px;
$firefox-viewer-toolbar-height: 32px;
$viewer-scrollbar-width: 16px;

.pdfPreview {
  height: 100%;
  position: relative;
}

iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.clickLayer {
  position: absolute;
  inset: $chrome-viewer-toolbar-height $viewer-scrollbar-width 0 0;
  z-index: 1;
  cursor: pointer;

  // Firefox only.
  @supports (-moz-appearance: none) {
    top: $firefox-viewer-toolbar-height;
  }

  // Safari (WebKit) only: its PDF viewer has no toolbar on top.
  @supports (font: -apple-system-body) and (-webkit-appearance: none) {
    top: 0;
  }
}

.passThrough {
  pointer-events: none;
}

.mobileCloseButton {
  position: absolute;
  top: var(--dimension-200);
  left: var(--dimension-150);
  z-index: 2;
  display: none;
  width: var(--dimension-300) !important;
  height: var(--dimension-300) !important;

  @media (max-width: 478px) {
    display: inline-flex;
  }
}
</style>
